/**
 * Guest module — Stay Guests + Mess Guests (v16 Round 1)
 * Reuses beds + payments. Round 2 will add bed dropdowns, split payment UI,
 * extend/checkout dialogs and revenue drill-downs.
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserPlus, Plus, IndianRupee, LogOut, Clock, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar } from "@/components/PaginationBar";
import { useAuth } from "@/context/AuthContext";

const DURATIONS = [
  { v: "1", l: "1 Day" }, { v: "2", l: "2 Days" }, { v: "5", l: "5 Days" },
  { v: "7", l: "7 Days" }, { v: "10", l: "10 Days" }, { v: "15", l: "15 Days" },
  { v: "30", l: "30 Days" }, { v: "custom", l: "Custom" },
];
const MEALS = [
  { v: "breakfast", l: "Breakfast" }, { v: "lunch", l: "Lunch" },
  { v: "dinner", l: "Dinner" }, { v: "full_day", l: "Full Day" }, { v: "other", l: "Other" },
];

export default function Guest() {
  const [tab, setTab] = useState("stay");
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/guests/dashboard").then((r) => setStats(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="guest-page">
      <div>
        <div className="eyebrow text-muted-foreground">Hospitality</div>
        <h1 className="font-display text-4xl font-bold mt-1 flex items-center gap-2">
          <UserPlus className="w-8 h-8 text-primary" /> Guest
        </h1>
      </div>
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Active Stay Guests" value={stats.active_stay_guests} />
            <MiniStat label="Today's Stay Revenue" value={`₹${(stats.today_stay_revenue || 0).toLocaleString("en-IN")}`} />
            <MiniStat label="Today's Mess Guests" value={stats.today_mess_guests} />
            <MiniStat label="Today's Mess Revenue" value={`₹${(stats.today_mess_revenue || 0).toLocaleString("en-IN")}`} />
          </div>
          {/* v26 — Cash/Online breakdown for today's guest revenue */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="guest-breakdown">
            <MiniStat label="Stay · Cash" value={`₹${(stats.today_stay_cash || 0).toLocaleString("en-IN")}`} />
            <MiniStat label="Stay · Online" value={`₹${(stats.today_stay_online || 0).toLocaleString("en-IN")}`} />
            <MiniStat label="Mess · Cash" value={`₹${(stats.today_mess_cash || 0).toLocaleString("en-IN")}`} />
            <MiniStat label="Mess · Online" value={`₹${(stats.today_mess_online || 0).toLocaleString("en-IN")}`} />
          </div>
        </>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stay" data-testid="tab-stay">Stay Guests</TabsTrigger>
          <TabsTrigger value="mess" data-testid="tab-mess">Mess Guests</TabsTrigger>
        </TabsList>
        <TabsContent value="stay" className="mt-4"><StayGuestsPanel /></TabsContent>
        <TabsContent value="mess" className="mt-4"><MessGuestsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <Card className="p-4 border-border bg-white">
    <div className="eyebrow text-muted-foreground">{label}</div>
    <div className="font-display font-bold text-2xl mt-1">{value}</div>
  </Card>
);

/* ============ STAY GUESTS ============ */
function StayGuestsPanel() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit, ...(q && { q }), ...(status && { status }) });
    api.get(`/guests/stay?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [page, limit, q, status]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold">Stay Guests</h2>
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="Search name / mobile / ID" value={q} className="w-56"
            onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="stay-search" />
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="stay-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setOpen(true)} data-testid="btn-add-stay">
            <Plus className="w-4 h-4 mr-1" /> Add Stay Guest
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="stay-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Guest ID</th><th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Mobile</th><th className="py-2 pr-3">Start</th>
            <th className="py-2 pr-3">End</th><th className="py-2 pr-3 text-right">Fee</th>
            <th className="py-2 pr-3 text-right">Paid</th>
            <th className="py-2 pr-3">Mode</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3"></th>
          </tr></thead>
          <tbody>
            {data.items.map((g) => (
              <StayGuestRow key={g.id} g={g} onChanged={load} />
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">No stay guests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} total={data.total} limit={limit} totalPages={data.total_pages}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
      {open && <AddStayGuestDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </Card>
  );
}

function StayGuestRow({ g, onChanged }) {
  const { user } = useAuth();
  const canEdit = ["admin", "hostel_manager", "warden"].includes(user?.role);
  const [payOpen, setPayOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const doCheckout = async () => {
    if (!window.confirm(`Check out ${g.name}? Bed will be released.`)) return;
    try { await api.post(`/guests/stay/${g.id}/checkout`); toast.success("Checked out"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/guests/stay/${g.id}`); toast.success("Guest archived"); setDelOpen(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const bal = Number(g.fee || 0) - Number(g.paid_amount || 0);
  const tone = g.status === "overdue" ? "destructive" : g.status === "checked_out" ? "secondary" : "default";
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="py-2 pr-3 font-mono text-xs">{g.guest_id}</td>
      <td className="py-2 pr-3 font-medium">{g.name}</td>
      <td className="py-2 pr-3">{g.mobile}</td>
      <td className="py-2 pr-3">{g.start_date}</td>
      <td className="py-2 pr-3">{g.end_date}</td>
      <td className="py-2 pr-3 text-right font-mono">₹{Number(g.fee).toLocaleString("en-IN")}</td>
      <td className="py-2 pr-3 text-right font-mono">₹{Number(g.paid_amount || 0).toLocaleString("en-IN")}</td>
      <td className="py-2 pr-3 capitalize text-xs" data-testid={`stay-mode-${g.id}`}>{g.payment_mode ? g.payment_mode.replace("_", " ") : "—"}</td>
      <td className="py-2 pr-3"><Badge variant={tone} className="capitalize">{g.status}</Badge></td>
      <td className="py-2 pr-3 text-right">
        <div className="flex gap-1 justify-end flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => setViewOpen(true)} data-testid={`view-stay-${g.id}`} title="View">
            <Eye className="w-3 h-3" />
          </Button>
          {bal > 0 && g.status !== "checked_out" && canEdit && (
            <Button size="sm" variant="outline" onClick={() => setPayOpen(true)} data-testid={`pay-${g.id}`} title="Pay">
              <IndianRupee className="w-3 h-3" />
            </Button>
          )}
          {g.status !== "checked_out" && canEdit && (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid={`edit-stay-${g.id}`} title="Edit">
                <Pencil className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setExtOpen(true)} data-testid={`extend-${g.id}`} title="Extend">
                <Clock className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={doCheckout} data-testid={`checkout-${g.id}`} title="Checkout">
                <LogOut className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => setDelOpen(true)} data-testid={`delete-stay-${g.id}`} title="Delete">
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
        {payOpen && <PayDialog gid={g.id} balance={bal}
          onClose={() => setPayOpen(false)} onDone={() => { setPayOpen(false); onChanged(); }} />}
        {extOpen && <ExtendDialog gid={g.id}
          onClose={() => setExtOpen(false)} onDone={() => { setExtOpen(false); onChanged(); }} />}
        {viewOpen && <ViewStayGuestDialog gid={g.id} onClose={() => setViewOpen(false)} />}
        {editOpen && <EditStayGuestDialog guest={g}
          onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); onChanged(); }} />}
        {delOpen && <ConfirmDialog
          title="Archive Stay Guest?"
          message={`Archive ${g.name} (${g.guest_id})? The bed (if any) will be released. Historical payments are preserved. This can only be reversed by an admin.`}
          confirmLabel="Archive"
          onConfirm={doDelete} onClose={() => setDelOpen(false)} tid={`confirm-del-stay-${g.id}`} />}
      </td>
    </tr>
  );
}

function AddStayGuestDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", mobile: "", gender: "male", id_details: "",
    start_date: new Date().toISOString().slice(0, 10), duration_type: "1",
    duration_days: "", fee: "", notes: "",
    room_id: "", bed_id: "", building_id: "", floor_id: "",
    // v26 — initial advance capture + mandatory mode when > 0
    initial_paid_amount: "", payment_mode: "cash",
    online_mode: "upi", transaction_id: "" });
  const [saving, setSaving] = useState(false);

  // Bed allocation cache (same shape/logic as Residents.jsx dialog)
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [floors, setFloors] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [bedsLoaded, setBedsLoaded] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/rooms"), api.get("/beds"), api.get("/floors"), api.get("/buildings")])
      .then(([rr, bb, ff, bg]) => {
        setRooms(rr.data || []); setBeds(bb.data || []);
        setFloors(ff.data || []); setBuildings(bg.data || []);
        setBedsLoaded(true);
      }).catch(() => setBedsLoaded(true));
  }, []);
  const roomsById = Object.fromEntries(rooms.map((r) => [r.id, r]));
  const floorById = Object.fromEntries(floors.map((f) => [f.id, f]));
  const buildingById = Object.fromEntries(buildings.map((b) => [b.id, b]));
  const bedsAvail = beds.filter((b) =>
    b.status === "available" && !b.resident_id && !b.stay_guest_id
  );
  const vacantBedsByRoom = bedsAvail.reduce((acc, b) => {
    (acc[b.room_id] ||= []).push(b); return acc;
  }, {});
  Object.values(vacantBedsByRoom).forEach((arr) =>
    arr.sort((a, b) => a.bed_no.localeCompare(b.bed_no, undefined, { numeric: true }))
  );
  const vacantRooms = rooms
    .filter((rm) => vacantBedsByRoom[rm.id]?.length > 0)
    .sort((a, b) => {
      const bA = buildingById[a.building_id]?.name || "";
      const bB = buildingById[b.building_id]?.name || "";
      if (bA !== bB) return bA.localeCompare(bB);
      const fA = floorById[a.floor_id]?.name || "";
      const fB = floorById[b.floor_id]?.name || "";
      if (fA !== fB) return fA.localeCompare(fB);
      return a.room_no.localeCompare(b.room_no, undefined, { numeric: true });
    });
  const roomLabel = (rm) => {
    const bldg = buildingById[rm.building_id]?.name;
    const flr = floorById[rm.floor_id]?.name;
    const parts = [`Room ${rm.room_no}`];
    if (flr) parts.push(flr);
    if (bldg) parts.push(bldg);
    return parts.join(" · ");
  };

  const save = async () => {
    if (!f.name || !f.mobile || !f.start_date || !f.fee)
      return toast.error("Name, mobile, start date and fee are required");
    const initPaid = Number(f.initial_paid_amount || 0);
    if (initPaid < 0) return toast.error("Initial paid amount cannot be negative");
    if (initPaid > Number(f.fee)) return toast.error("Initial paid cannot exceed fee");
    if (initPaid > 0 && !f.payment_mode) return toast.error("Payment mode is required for the paid amount");
    // Resolve final mode: Cash → "cash", Online → chosen sub-mode
    const finalMode = initPaid > 0
      ? (f.payment_mode === "online" ? f.online_mode : f.payment_mode)
      : null;
    setSaving(true);
    try {
      await api.post("/guests/stay", { ...f, fee: Number(f.fee),
        duration_days: f.duration_type === "custom" ? Number(f.duration_days) : null,
        building_id: f.building_id || null, floor_id: f.floor_id || null,
        room_id: f.room_id || null, bed_id: f.bed_id || null,
        initial_paid_amount: initPaid,
        payment_mode: finalMode,
        transaction_id: f.transaction_id || null });
      toast.success("Stay guest added"); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="add-stay-dialog">
        <DialogHeader><DialogTitle>New Stay Guest</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *"><Input value={f.name} data-testid="sg-name" onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Mobile *"><Input value={f.mobile} data-testid="sg-mobile" onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Gender"><Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
            <SelectTrigger data-testid="sg-gender"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
          </Select></Field>
          <Field label="ID Details"><Input value={f.id_details} onChange={(e) => setF({ ...f, id_details: e.target.value })} /></Field>
          <Field label="Start Date *"><Input type="date" value={f.start_date} data-testid="sg-start" onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
          <Field label="Duration *"><Select value={f.duration_type} onValueChange={(v) => setF({ ...f, duration_type: v })}>
            <SelectTrigger data-testid="sg-duration"><SelectValue /></SelectTrigger>
            <SelectContent>{DURATIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
          </Select></Field>
          {f.duration_type === "custom" && (
            <Field label="Custom Days *"><Input type="number" min="1" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} /></Field>
          )}
          <Field label="Fee (₹) *"><Input type="number" value={f.fee} data-testid="sg-fee" onChange={(e) => setF({ ...f, fee: e.target.value })} /></Field>

          {/* Bed allotment — Building → Floor → Room → Bed (same as Bed Allocation) */}
          <Field label="Room (optional)">
            <Select value={f.room_id || ""}
              onValueChange={(rid) => {
                const rm = roomsById[rid];
                setF({ ...f,
                  room_id: rid,
                  building_id: rm?.building_id || "",
                  floor_id: rm?.floor_id || "",
                  bed_id: "" });
              }}
              disabled={!bedsLoaded || vacantRooms.length === 0}>
              <SelectTrigger data-testid="sg-room"><SelectValue placeholder={
                !bedsLoaded ? "Loading rooms…"
                : vacantRooms.length === 0 ? "No vacant rooms"
                : "Select vacant room"
              } /></SelectTrigger>
              <SelectContent>
                {vacantRooms.map((rm) => (
                  <SelectItem key={rm.id} value={rm.id}>
                    {roomLabel(rm)} · {vacantBedsByRoom[rm.id].length} vacant
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Bed (optional)">
            <Select value={f.bed_id || ""}
              onValueChange={(bid) => setF({ ...f, bed_id: bid })}
              disabled={!f.room_id}>
              <SelectTrigger data-testid="sg-bed"><SelectValue placeholder={
                f.room_id ? "Select vacant bed" : "Pick room first"
              } /></SelectTrigger>
              <SelectContent>
                {(vacantBedsByRoom[f.room_id] || []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.bed_no}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* v26 — Initial payment capture with mandatory Cash/Online mode */}
          <div className="col-span-2 pt-2 mt-2 border-t border-border">
            <div className="eyebrow text-muted-foreground mb-2">Initial Payment (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Initial Paid Amount (₹)">
                <Input type="number" min="0" value={f.initial_paid_amount}
                  data-testid="sg-init-paid"
                  onChange={(e) => setF({ ...f, initial_paid_amount: e.target.value })} />
              </Field>
              <Field label={Number(f.initial_paid_amount || 0) > 0 ? "Payment Mode *" : "Payment Mode"}>
                <Select value={f.payment_mode}
                  onValueChange={(v) => setF({ ...f, payment_mode: v })}>
                  <SelectTrigger data-testid="sg-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {f.payment_mode === "online" && Number(f.initial_paid_amount || 0) > 0 && (
                <>
                  <Field label="Online Method">
                    <Select value={f.online_mode}
                      onValueChange={(v) => setF({ ...f, online_mode: v })}>
                      <SelectTrigger data-testid="sg-online-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="razorpay">Razorpay</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Transaction ID / Reference">
                    <Input value={f.transaction_id} data-testid="sg-txn-id"
                      onChange={(e) => setF({ ...f, transaction_id: e.target.value })} />
                  </Field>
                </>
              )}
            </div>
          </div>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="sg-save">{saving ? "Saving…" : "Add Guest"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ gid, balance, onClose, onDone }) {
  const [mode, setMode] = useState("cash");           // cash|upi|card|bank_transfer|split
  const [amt, setAmt] = useState(String(balance));
  const [cashAmt, setCashAmt] = useState("");
  const [onlineAmt, setOnlineAmt] = useState("");
  const [onlineMode, setOnlineMode] = useState("upi");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (mode === "split") {
        const c = Number(cashAmt || 0), o = Number(onlineAmt || 0);
        if (c <= 0 && o <= 0) { toast.error("Enter cash or online amount"); setSaving(false); return; }
        await api.post(`/guests/stay/${gid}/split-payment`, {
          cash_amount: c, online_amount: o, online_mode: onlineMode, reference: ref || null });
      } else {
        await api.post(`/guests/stay/${gid}/payment`, {
          amount: Number(amt), mode, reference: ref || null });
      }
      toast.success("Payment recorded"); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pay-stay-dialog">
        <DialogHeader><DialogTitle>Record Payment · Balance ₹{Number(balance).toLocaleString("en-IN")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Mode">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank_transfer">Bank</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="split">Split (Cash + Online)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {mode !== "split" ? (
            <Field label="Amount"><Input type="number" value={amt} data-testid="pay-amount" onChange={(e) => setAmt(e.target.value)} /></Field>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cash Amount"><Input type="number" value={cashAmt} data-testid="pay-cash" onChange={(e) => setCashAmt(e.target.value)} /></Field>
              <Field label="Online Amount"><Input type="number" value={onlineAmt} data-testid="pay-online" onChange={(e) => setOnlineAmt(e.target.value)} /></Field>
              <div className="col-span-2">
                <Field label="Online Mode">
                  <Select value={onlineMode} onValueChange={setOnlineMode}>
                    <SelectTrigger data-testid="pay-online-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}
          <Field label="Reference / Transaction ID"><Input value={ref} onChange={(e) => setRef(e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="pay-save">{saving ? "Recording…" : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtendDialog({ gid, onClose, onDone }) {
  const [days, setDays] = useState("");
  const [fee, setFee] = useState("");
  const save = async () => {
    try {
      await api.post(`/guests/stay/${gid}/extend`, { extra_days: Number(days), fee: Number(fee || 0) });
      toast.success("Stay extended"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle>Extend Stay</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Extra Days *"><Input type="number" value={days} onChange={(e) => setDays(e.target.value)} /></Field>
          <Field label="Additional Fee (₹)"><Input type="number" value={fee} onChange={(e) => setFee(e.target.value)} /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save}>Extend</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ MESS GUESTS ============ */
function MessGuestsPanel() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit, ...(q && { q }) });
    api.get(`/guests/mess?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [page, limit, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold">Mess Guests</h2>
        <div className="flex gap-2">
          <Input placeholder="Search name / mobile" value={q} className="w-56"
            onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="mess-search" />
          <Button onClick={() => setOpen(true)} data-testid="btn-add-mess">
            <Plus className="w-4 h-4 mr-1" /> Add Mess Guest
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="mess-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Guest ID</th><th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Mobile</th><th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Meal</th><th className="py-2 pr-3 text-right">Meals</th>
            <th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Mode</th>
            <th className="py-2 pr-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {data.items.map((g) => (
              <MessGuestRow key={g.id} g={g} onChanged={load} />
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">No mess guests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} total={data.total} limit={limit} totalPages={data.total_pages}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
      {open && <AddMessGuestDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </Card>
  );
}

function MessGuestRow({ g, onChanged }) {
  const { user } = useAuth();
  const canEdit = ["admin", "hostel_manager", "warden", "accountant"].includes(user?.role);
  const canDelete = ["admin", "hostel_manager", "warden"].includes(user?.role);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const doDelete = async () => {
    try { await api.delete(`/guests/mess/${g.id}`); toast.success("Guest archived"); setDelOpen(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="py-2 pr-3 font-mono text-xs">{g.guest_id}</td>
      <td className="py-2 pr-3 font-medium">{g.name}</td>
      <td className="py-2 pr-3">{g.mobile || "—"}</td>
      <td className="py-2 pr-3">{g.date}</td>
      <td className="py-2 pr-3 capitalize">{g.meal_type.replace("_", " ")}</td>
      <td className="py-2 pr-3 text-right">{g.meal_count}</td>
      <td className="py-2 pr-3 text-right font-mono">₹{Number(g.amount).toLocaleString("en-IN")}</td>
      <td className="py-2 pr-3 capitalize">{g.payment_mode}</td>
      <td className="py-2 pr-3 text-right">
        <div className="flex gap-1 justify-end flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => setViewOpen(true)} data-testid={`view-mess-${g.id}`} title="View">
            <Eye className="w-3 h-3" />
          </Button>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid={`edit-mess-${g.id}`} title="Edit">
              <Pencil className="w-3 h-3" />
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="outline"
              className="text-destructive hover:bg-destructive/10"
              onClick={() => setDelOpen(true)} data-testid={`delete-mess-${g.id}`} title="Delete">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        {viewOpen && <ViewMessGuestDialog gid={g.id} onClose={() => setViewOpen(false)} />}
        {editOpen && <EditMessGuestDialog guest={g}
          onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); onChanged(); }} />}
        {delOpen && <ConfirmDialog
          title="Archive Mess Guest?"
          message={`Archive ${g.name} (${g.guest_id})? Historical payments (₹${Number(g.amount).toLocaleString("en-IN")}) are preserved. This can only be reversed by an admin.`}
          confirmLabel="Archive"
          onConfirm={doDelete} onClose={() => setDelOpen(false)} tid={`confirm-del-mess-${g.id}`} />}
      </td>
    </tr>
  );
}

function AddMessGuestDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", mobile: "",
    date: new Date().toISOString().slice(0, 10), meal_type: "lunch",
    meal_count: 1, amount: "", payment_mode: "cash",
    cash_amount: "", online_amount: "", transaction_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name || !f.amount) return toast.error("Name and amount are required");
    setSaving(true);
    try {
      const payload = { ...f, amount: Number(f.amount), meal_count: Number(f.meal_count),
        cash_amount: f.cash_amount ? Number(f.cash_amount) : null,
        online_amount: f.online_amount ? Number(f.online_amount) : null };
      await api.post("/guests/mess", payload);
      toast.success("Mess guest added"); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="add-mess-dialog">
        <DialogHeader><DialogTitle>New Mess Guest</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *"><Input value={f.name} data-testid="mg-name" onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Mobile"><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Meal Type"><Select value={f.meal_type} onValueChange={(v) => setF({ ...f, meal_type: v })}>
            <SelectTrigger data-testid="mg-meal"><SelectValue /></SelectTrigger>
            <SelectContent>{MEALS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select></Field>
          <Field label="Meals"><Input type="number" min="1" value={f.meal_count} onChange={(e) => setF({ ...f, meal_count: e.target.value })} /></Field>
          <Field label="Amount (₹) *"><Input type="number" value={f.amount} data-testid="mg-amount" onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <Field label="Payment Mode"><Select value={f.payment_mode} onValueChange={(v) => setF({ ...f, payment_mode: v })}>
            <SelectTrigger data-testid="mg-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="bank_transfer">Bank</SelectItem>
              <SelectItem value="split">Split (Cash + Online)</SelectItem>
            </SelectContent>
          </Select></Field>
          <Field label="Transaction ID"><Input value={f.transaction_id} onChange={(e) => setF({ ...f, transaction_id: e.target.value })} /></Field>
          {f.payment_mode === "split" && <>
            <Field label="Cash Amount"><Input type="number" value={f.cash_amount} onChange={(e) => setF({ ...f, cash_amount: e.target.value })} /></Field>
            <Field label="Online Amount"><Input type="number" value={f.online_amount} onChange={(e) => setF({ ...f, online_amount: e.target.value })} /></Field>
          </>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mg-save">{saving ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/* ============ Shared: Confirm dialog ============ */
function ConfirmDialog({ title, message, confirmLabel = "Confirm", onConfirm, onClose, tid }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={tid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{message}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={busy}
            data-testid={`${tid}-confirm`}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ View: Stay Guest ============ */
function ViewStayGuestDialog({ gid, onClose }) {
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/guests/stay/${gid}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [gid]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="view-stay-dialog">
        <DialogHeader><DialogTitle>Stay Guest Detail</DialogTitle></DialogHeader>
        {g === null && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
        {g === false && <div className="py-8 text-center text-destructive">Failed to load.</div>}
        {g && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Row label="Guest ID" value={g.guest_id} />
              <Row label="Status" value={<Badge className="capitalize">{g.status}</Badge>} />
              <Row label="Name" value={g.name} />
              <Row label="Mobile" value={g.mobile} />
              <Row label="Gender" value={g.gender} />
              <Row label="ID Details" value={g.id_details || "—"} />
              <Row label="Start Date" value={g.start_date} />
              <Row label="End Date" value={g.end_date} />
              <Row label="Duration" value={`${g.days} day(s)`} />
              <Row label="Fee" value={`₹${Number(g.fee || 0).toLocaleString("en-IN")}`} />
              <Row label="Paid" value={`₹${Number(g.paid_amount || 0).toLocaleString("en-IN")}`} />
              <Row label="Balance" value={`₹${Math.max(0, Number(g.fee || 0) - Number(g.paid_amount || 0)).toLocaleString("en-IN")}`} />
              <Row label="Payment Mode" value={<span className="capitalize" data-testid="view-stay-mode">{g.payment_mode ? g.payment_mode.replace("_", " ") : "—"}</span>} />
              <Row label="Bed" value={g.bed_id ? "Assigned" : "—"} />
              <Row label="Notes" value={g.notes || "—"} full />
            </div>
            <div>
              <div className="eyebrow text-muted-foreground mb-2">Payments ({(g.payments || []).length})</div>
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {(g.payments || []).length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">No payments yet.</div>}
                {(g.payments || []).map((p) => (
                  <div key={p.id} className="flex justify-between px-3 py-1.5 border-b last:border-0 text-xs">
                    <span className="capitalize">{p.mode} · {p.created_at?.slice(0, 10)}</span>
                    <span className="font-mono">₹{Number(p.amount).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const Row = ({ label, value, full = false }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="eyebrow text-muted-foreground">{label}</div>
    <div className="mt-0.5">{value}</div>
  </div>
);

/* ============ Edit: Stay Guest ============ */
function EditStayGuestDialog({ guest, onClose, onDone }) {
  const [f, setF] = useState({
    name: guest.name || "", mobile: guest.mobile || "", gender: guest.gender || "male",
    id_details: guest.id_details || "", start_date: guest.start_date || "",
    duration_type: guest.duration_type || "1",
    duration_days: guest.duration_days || "", fee: String(guest.fee || ""), notes: guest.notes || "",
    room_id: guest.room_id || "", bed_id: guest.bed_id || "",
    building_id: guest.building_id || "", floor_id: guest.floor_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [floors, setFloors] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/rooms"), api.get("/beds"), api.get("/floors"), api.get("/buildings")])
      .then(([rr, bb, ff, bg]) => {
        setRooms(rr.data || []); setBeds(bb.data || []);
        setFloors(ff.data || []); setBuildings(bg.data || []);
        setLoaded(true);
      }).catch(() => setLoaded(true));
  }, []);
  const roomsById = Object.fromEntries(rooms.map((r) => [r.id, r]));
  const floorById = Object.fromEntries(floors.map((fl) => [fl.id, fl]));
  const buildingById = Object.fromEntries(buildings.map((b) => [b.id, b]));
  // Available beds = available OR the guest's own current bed
  const bedsAvail = beds.filter((b) =>
    (b.status === "available" && !b.resident_id && !b.stay_guest_id) || b.id === guest.bed_id
  );
  const vacantBedsByRoom = bedsAvail.reduce((acc, b) => { (acc[b.room_id] ||= []).push(b); return acc; }, {});
  const vacantRooms = rooms.filter((rm) => vacantBedsByRoom[rm.id]?.length > 0);
  const roomLabel = (rm) => {
    const bldg = buildingById[rm.building_id]?.name;
    const flr = floorById[rm.floor_id]?.name;
    const parts = [`Room ${rm.room_no}`];
    if (flr) parts.push(flr);
    if (bldg) parts.push(bldg);
    return parts.join(" · ");
  };
  const save = async () => {
    if (!f.name || !f.mobile || !f.start_date || !f.fee)
      return toast.error("Name, mobile, start date and fee are required");
    setSaving(true);
    try {
      const payload = {
        name: f.name, mobile: f.mobile, gender: f.gender, id_details: f.id_details,
        start_date: f.start_date, duration_type: f.duration_type,
        duration_days: f.duration_type === "custom" ? Number(f.duration_days) : null,
        fee: Number(f.fee), notes: f.notes,
        building_id: f.building_id || null, floor_id: f.floor_id || null,
        room_id: f.room_id || null, bed_id: f.bed_id || null,
      };
      await api.put(`/guests/stay/${guest.id}`, payload);
      toast.success("Guest updated"); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="edit-stay-dialog">
        <DialogHeader><DialogTitle>Edit Stay Guest · {guest.guest_id}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *"><Input value={f.name} data-testid="esg-name" onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Mobile *"><Input value={f.mobile} data-testid="esg-mobile" onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Gender">
            <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
              <SelectTrigger data-testid="esg-gender"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="ID Details"><Input value={f.id_details} onChange={(e) => setF({ ...f, id_details: e.target.value })} /></Field>
          <Field label="Start Date *"><Input type="date" value={f.start_date} data-testid="esg-start" onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
          <Field label="Duration *">
            <Select value={f.duration_type} onValueChange={(v) => setF({ ...f, duration_type: v })}>
              <SelectTrigger data-testid="esg-duration"><SelectValue /></SelectTrigger>
              <SelectContent>{DURATIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.duration_type === "custom" && (
            <Field label="Custom Days *"><Input type="number" min="1" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} /></Field>
          )}
          <Field label="Fee (₹) *"><Input type="number" value={f.fee} data-testid="esg-fee" onChange={(e) => setF({ ...f, fee: e.target.value })} /></Field>
          <Field label="Room">
            <Select value={f.room_id || "none"} onValueChange={(rid) => {
                if (rid === "none") { setF({ ...f, room_id: "", bed_id: "", building_id: "", floor_id: "" }); return; }
                const rm = roomsById[rid];
                setF({ ...f, room_id: rid, building_id: rm?.building_id || "", floor_id: rm?.floor_id || "", bed_id: "" });
              }} disabled={!loaded}>
              <SelectTrigger data-testid="esg-room"><SelectValue placeholder={loaded ? "Select room" : "Loading…"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No Bed —</SelectItem>
                {vacantRooms.map((rm) => (
                  <SelectItem key={rm.id} value={rm.id}>{roomLabel(rm)} · {vacantBedsByRoom[rm.id].length} vacant</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Bed">
            <Select value={f.bed_id || ""} onValueChange={(bid) => setF({ ...f, bed_id: bid })} disabled={!f.room_id}>
              <SelectTrigger data-testid="esg-bed"><SelectValue placeholder={f.room_id ? "Select bed" : "Pick room first"} /></SelectTrigger>
              <SelectContent>
                {(vacantBedsByRoom[f.room_id] || []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.bed_no}{b.id === guest.bed_id ? " (current)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="esg-save">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ View: Mess Guest ============ */
function ViewMessGuestDialog({ gid, onClose }) {
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/guests/mess/${gid}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [gid]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="view-mess-dialog">
        <DialogHeader><DialogTitle>Mess Guest Detail</DialogTitle></DialogHeader>
        {g === null && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
        {g === false && <div className="py-8 text-center text-destructive">Failed to load.</div>}
        {g && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Row label="Guest ID" value={g.guest_id} />
              <Row label="Name" value={g.name} />
              <Row label="Mobile" value={g.mobile || "—"} />
              <Row label="Date" value={g.date} />
              <Row label="Meal Type" value={<span className="capitalize">{g.meal_type.replace("_", " ")}</span>} />
              <Row label="Meals" value={g.meal_count} />
              <Row label="Amount" value={`₹${Number(g.amount).toLocaleString("en-IN")}`} />
              <Row label="Payment Mode" value={<span className="capitalize">{g.payment_mode}</span>} />
              <Row label="Notes" value={g.notes || "—"} full />
            </div>
            <div>
              <div className="eyebrow text-muted-foreground mb-2">Payments ({(g.payments || []).length})</div>
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {(g.payments || []).length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">No payments recorded.</div>}
                {(g.payments || []).map((p) => (
                  <div key={p.id} className="flex justify-between px-3 py-1.5 border-b last:border-0 text-xs">
                    <span className="capitalize">{p.mode} · {p.created_at?.slice(0, 10)}</span>
                    <span className="font-mono">₹{Number(p.amount).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Edit: Mess Guest ============ */
function EditMessGuestDialog({ guest, onClose, onDone }) {
  const [f, setF] = useState({
    name: guest.name || "", mobile: guest.mobile || "", date: guest.date || "",
    meal_type: guest.meal_type || "lunch", meal_count: guest.meal_count || 1,
    amount: String(guest.amount || ""), notes: guest.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name || !f.amount) return toast.error("Name and amount are required");
    setSaving(true);
    try {
      await api.put(`/guests/mess/${guest.id}`, {
        name: f.name, mobile: f.mobile || null, date: f.date, meal_type: f.meal_type,
        meal_count: Number(f.meal_count), amount: Number(f.amount), notes: f.notes || null,
      });
      toast.success("Guest updated"); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-mess-dialog">
        <DialogHeader><DialogTitle>Edit Mess Guest · {guest.guest_id}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *"><Input value={f.name} data-testid="emg-name" onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Mobile"><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Meal Type">
            <Select value={f.meal_type} onValueChange={(v) => setF({ ...f, meal_type: v })}>
              <SelectTrigger data-testid="emg-meal"><SelectValue /></SelectTrigger>
              <SelectContent>{MEALS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Meals"><Input type="number" min="1" value={f.meal_count} onChange={(e) => setF({ ...f, meal_count: e.target.value })} /></Field>
          <Field label="Amount (₹) *"><Input type="number" value={f.amount} data-testid="emg-amount" onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
        </div>
        <p className="text-xs text-muted-foreground">Historical payment records are preserved — only the guest details above are updated.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="emg-save">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
